import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  X,
  Info,
  CheckCircle,
  FileText,
  Users,
  ClipboardList,
  MapPin,
  Phone,
  Mail,
} from "lucide-react";
import { createPlan, handleApiError } from "../../api/procurementPlanApi";
import { getVendors } from "../../api/vendorApi";
import { getStaff } from "../../api/staff-calls-api";
import HoverCardPortal from "../../components/hoverCardComponent";

/**
 * EnhancedProcurementPlanModal
 */
export default function EnhancedProcurementPlanModal({ isOpen, onClose, project, itemsToProcure }) {
  /* --------------------------------------------------------------------------
     State: plan-level fields
     -------------------------------------------------------------------------- */
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [procurementMethod, setProcurementMethod] = useState("Request for Quotation (RFQ)");
  const [justification, setJustification] = useState("");
  const [financials, setFinancials] = useState({ taxes: 0, contingency: 0 });
  const [planProcurementOfficer, setPlanProcurementOfficer] = useState("");

  /* --------------------------------------------------------------------------
     State: item-level strategies, data fetched from APIs, UI state
     -------------------------------------------------------------------------- */
  const [itemStrategies, setItemStrategies] = useState({});
  const [vendors, setVendors] = useState([]);
  const [allStaff, setAllStaff] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [infoMessage, setInfoMessage] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});

  // Hover card state
  const [hoveredVendorId, setHoveredVendorId] = useState(null);
  const [hoverCardPosition, setHoverCardPosition] = useState({});

  // Refs for click-outside and message scrolling
  const modalRef = useRef(null);
  const messageContainerRef = useRef(null);
  const formRef = useRef(null);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  /* --------------------------------------------------------------------------
     Derived data (memoized)
     -------------------------------------------------------------------------- */

  // Staff filtered to procurement officers (operations_associate)
  const procurementOfficers = useMemo(
    () => allStaff.filter((staff) => staff.role === "operations_associate"),
    [allStaff]
  );

  // Date constraints derived from itemsToProcure (earliest needed by)
  const dateConstraints = useMemo(() => {
    if (!itemsToProcure || itemsToProcure.length === 0) return {};
    
    // Filter out invalid dates
    const validDates = itemsToProcure
      .map((item) => new Date(item.neededBy))
      .filter(date => !isNaN(date.getTime()));
    
    if (validDates.length === 0) return {};
    
    const earliestNeededBy = new Date(Math.min(...validDates.map(date => date.getTime())));
    
    return {
      earliestNeededBy,
      maxEndDate: earliestNeededBy.toISOString().split("T")[0],
    };
  }, [itemsToProcure]);

  // Hints based on timeline choices
const dateHints = useMemo(() => {
  const hints = [
    "Ensure procurement starts well before items are needed to allow sufficient time for processing and delivery.",
  ];

  // Only proceed if dates are valid
  const startDateObj = new Date(startDate);
  const endDateObj = new Date(endDate);
  const maxEndDateObj = dateConstraints.maxEndDate ? new Date(dateConstraints.maxEndDate) : null;

  if (isNaN(startDateObj.getTime()) || isNaN(endDateObj.getTime())) {
    return hints;
  }

  if (endDate && maxEndDateObj && !isNaN(maxEndDateObj.getTime()) && endDateObj > maxEndDateObj) {
    hints.push("Warning: Plan ends after items are needed! Delivery will be late.");
  }

  if (endDate && maxEndDateObj && !isNaN(maxEndDateObj.getTime())) {
    const daysDiff = (maxEndDateObj - endDateObj) / (1000 * 60 * 60 * 24);
    if (daysDiff < 14) hints.push("Warning: Less than 2 weeks between procurement end and when items are needed.");
  }

  if (startDate && endDate) {
    const daysDiff = (endDateObj - startDateObj) / (1000 * 60 * 60 * 24);
    if (daysDiff < 7) hints.push("Warning: Procurement period is less than a week.");
  }

  return hints;
}, [startDate, endDate, dateConstraints]);

  // Filter vendors by selectedCategory
  const filteredVendors = useMemo(() => {
    if (!Array.isArray(vendors)) return [];
    return vendors.filter((vendor) => !selectedCategory || vendor.category === selectedCategory);
  }, [vendors, selectedCategory]);

  // Totals
  const subtotal = useMemo(() => {
    return (itemsToProcure || []).reduce((sum, item) => sum + (item.totalCost || 0), 0);
  }, [itemsToProcure]);

  const grandTotal = useMemo(() => subtotal + Number(financials.taxes || 0) + Number(financials.contingency || 0), [
    subtotal,
    financials,
  ]);

  /* --------------------------------------------------------------------------
     Effects: initialize itemStrategies when items change
     -------------------------------------------------------------------------- */
  useEffect(() => {
    if (itemsToProcure && itemsToProcure.length > 0) {
      const initial = {};
      itemsToProcure.forEach((item) => {
        initial[item._id] = {
          recommendedSupplier: "",
          justification: "",
          plannedOrderDate: "",
          riskLevel: "Low",
          technicalApprover: "",
        };
      });
      setItemStrategies(initial);
    } else {
      setItemStrategies({});
    }
  }, [itemsToProcure]);

  /* --------------------------------------------------------------------------
     Effects: fetch vendors and staff when modal opens
     -------------------------------------------------------------------------- */
  useEffect(() => {
    if (!isOpen) return;

    let mounted = true;
    const fetchData = async () => {
      try {
        const vendorsResponse = await getVendors();
        if (!mounted) return;
        setVendors(vendorsResponse?.data?.data || vendorsResponse?.data || []);

        const staffResponse = await getStaff();
        if (!mounted) return;

        let staffList = [];
        if (Array.isArray(staffResponse)) {
          staffList = staffResponse;
        } else if (Array.isArray(staffResponse.data)) {
          staffList = staffResponse.data;
        } else if (Array.isArray(staffResponse.data?.staff)) {
          staffList = staffResponse.data.staff;
        } else if (Array.isArray(staffResponse.data?.docs)) {
          staffList = staffResponse.data.docs;
        } else if (Array.isArray(staffResponse.raw?.data)) {
          staffList = staffResponse.raw.data;
        } else if (Array.isArray(staffResponse.list)) {
          staffList = staffResponse.list;
        }

        setAllStaff(staffList);
      } catch (err) {
        console.error("Failed to fetch vendors/staff:", err);
        setVendors([]);
        setAllStaff([]);
      }
    };

    fetchData();
    return () => {
      mounted = false;
    };
  }, [isOpen]);

  /* --------------------------------------------------------------------------
     UI helpers: formatting & calculations
     -------------------------------------------------------------------------- */
  const formatDate = (dateString) => {
    if (!dateString) return "Not specified";
    
    // Handle invalid dates gracefully
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "Invalid date";
    
    return date.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
  };

  const calculateDuration = (start, end) => {
    if (!start || !end) return "N/A";
    
    const s = new Date(start);
    const e = new Date(end);
    
    // Check if dates are valid
    if (isNaN(s.getTime()) || isNaN(e.getTime())) return "Invalid date";
    
    const months = (e.getFullYear() - s.getFullYear()) * 12 + (e.getMonth() - s.getMonth());
    return `${months} months`;
  };

  /* --------------------------------------------------------------------------
     Transform API error messages to user-friendly format
     -------------------------------------------------------------------------- */
  const transformApiError = (error) => {
    if (!error.response || !error.response.data) return error;
    
    const { data } = error.response;
    
    // Handle validation errors with details array (original format)
    if (data.message === "Validation error" && Array.isArray(data.details)) {
      const transformedErrors = {};
      
      data.details.forEach(detail => {
        // Parse field path like "requirements.0.plannedOrderDate"
        const fieldMatch = detail.field.match(/^requirements\.(\d+)\.(.+)$/);
        
        if (fieldMatch) {
          const itemIndex = parseInt(fieldMatch[1], 10);
          const fieldName = fieldMatch[2];
          const item = itemsToProcure[itemIndex];
          
          if (item) {
            const fieldKey = `${item._id}-${fieldName}`;
            
            const fieldLabels = {
              plannedOrderDate: "Planned Order Date",
              recommendedSupplier: "Recommended Supplier",
              riskLevel: "Risk Level",
              technicalApprover: "Technical Approver"
            };
            
            const fieldLabel = fieldLabels[fieldName] || fieldName;
            
            let userFriendlyMessage = detail.message
              .replace(`requirements[${itemIndex}].${fieldName}`, `"${item.name}" - ${fieldLabel}`)
              .replace("must be a valid date", "must be a valid date format")
              .replace("is required", "is required");
            
            if (detail.message.includes("is required") || detail.message.includes("must not be empty")) {
              userFriendlyMessage = `Please enter a ${fieldLabel.toLowerCase()} for "${item.name}".`;
            }
            
            transformedErrors[fieldKey] = userFriendlyMessage;
          }
        }
      });
      
      return transformedErrors;
    }
    
    // Handle validation errors with numeric keys (new format)
    if (typeof data === 'object' && data !== null) {
      const errorEntries = Object.entries(data);
      
      if (errorEntries.some(([key]) => /^\d+$/.test(key))) {
        const transformedErrors = {};
        
        errorEntries.forEach(([key, value]) => {
          if (!/^\d+$/.test(key)) return;
          
          const errorDetail = value;
          if (errorDetail && errorDetail.field && errorDetail.message) {
            const fieldMatch = errorDetail.field.match(/^requirements\.(\d+)\.(.+)$/);
            
            if (fieldMatch) {
              const itemIndex = parseInt(fieldMatch[1], 10);
              const fieldName = fieldMatch[2];
              const item = itemsToProcure[itemIndex];
              
              if (item) {
                const fieldKey = `${item._id}-${fieldName}`;
                
                const fieldLabels = {
                  plannedOrderDate: "Planned Order Date",
                  recommendedSupplier: "Recommended Supplier",
                  riskLevel: "Risk Level",
                  technicalApprover: "Technical Approver"
                };
                
                const fieldLabel = fieldLabels[fieldName] || fieldName;
                
                let userFriendlyMessage = errorDetail.message
                  .replace(`requirements[${itemIndex}].${fieldName}`, `"${item.name}" - ${fieldLabel}`)
                  .replace("must be a valid date", "must be a valid date format")
                  .replace("is required", "is required");
                
                if (errorDetail.message.includes("is required") || errorDetail.message.includes("must not be empty")) {
                  userFriendlyMessage = `Please enter a ${fieldLabel.toLowerCase()} for "${item.name}".`;
                }
                
                transformedErrors[fieldKey] = userFriendlyMessage;
              }
            }
          }
        });
        
        return transformedErrors;
      }
    }
    
    return error;
  };

  /* --------------------------------------------------------------------------
     Hover card placement: choose top/bottom based on viewport space
     -------------------------------------------------------------------------- */
  const handleVendorHoverEnter = (e, itemId) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const spaceBelow = viewportHeight - rect.bottom;
    const position = spaceBelow < 200 ? "top" : "bottom";

    setHoverCardPosition({
      position,
      x: rect.left,
      y: position === "top" ? rect.top : rect.bottom,
    });
    setHoveredVendorId(itemId);
  };

  const handleVendorHoverLeave = () => {
    setHoveredVendorId(null);
    setHoverCardPosition({});
  };

  /* --------------------------------------------------------------------------
     Click outside modal to close
     -------------------------------------------------------------------------- */
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (modalRef.current && !modalRef.current.contains(e.target)) onClose();
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onClose]);

  /* --------------------------------------------------------------------------
     Reset form on open
     -------------------------------------------------------------------------- */
  useEffect(() => {
    if (isOpen) {
      setTitle("");
      setDescription("");
      setStartDate("");
      setEndDate("");
      setProcurementMethod("Request for Quotation (RFQ)");
      setJustification("");
      setFinancials({ taxes: 0, contingency: 0 });
      setPlanProcurementOfficer("");
      setSelectedCategory("");
      setSuccessMessage("");
      setErrorMessage("");
      setInfoMessage("");
      setFieldErrors({});
      setCurrentPage(1);
    }
  }, [isOpen]);

  /* --------------------------------------------------------------------------
     Function to scroll to an element with a specific ID or class
     -------------------------------------------------------------------------- */
  const scrollToElement = (elementIdOrClass) => {
    if (!modalRef.current) return;
    
    const element = elementIdOrClass.startsWith('.') 
      ? modalRef.current.querySelector(elementIdOrClass)
      : document.getElementById(elementIdOrClass);
      
    if (element) {
      element.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'center' 
      });
    }
  };

  /* --------------------------------------------------------------------------
     Function to scroll to the first error message in the modal
     -------------------------------------------------------------------------- */
  const scrollToFirstError = () => {
    if (!modalRef.current) return;
    
    // First check if there are field errors and scroll to the first one
    if (Object.keys(fieldErrors).length > 0) {
      const firstErrorKey = Object.keys(fieldErrors)[0];
      const [itemId, fieldName] = firstErrorKey.split('-');
      
      // Try to find the error message element
      const errorElement = modalRef.current.querySelector(`[data-error-for="${firstErrorKey}"]`);
      if (errorElement) {
        errorElement.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center' 
        });
        return;
      }
      
      // If no specific error element, try to find the input field
      const inputSelectors = [
        `input[name="${itemId}-${fieldName}"]`,
        `select[name="${itemId}-${fieldName}"]`,
        `textarea[name="${itemId}-${fieldName}"]`,
        `#${itemId}-${fieldName}`
      ];
      
      for (const selector of inputSelectors) {
        const inputElement = modalRef.current.querySelector(selector);
        if (inputElement) {
          inputElement.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'center' 
          });
          return;
        }
      }
    }
    
    // Look for common error message patterns
    const errorSelectors = [
      '.error-message',
      '.field-error',
      '.error-text',
      '[data-error="true"]',
      '.text-red-600',
      '.bg-red-50'
    ];
    
    for (const selector of errorSelectors) {
      const errorElement = modalRef.current.querySelector(selector);
      if (errorElement) {
        errorElement.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center' 
        });
        return;
      }
    }
    
    // If no field errors, scroll to the general error message
    if (messageContainerRef.current) {
      messageContainerRef.current.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'center' 
      });
    }
  };

  /* --------------------------------------------------------------------------
     Scroll to message when it appears
     -------------------------------------------------------------------------- */
  useEffect(() => {
    const scrollToMessage = () => {
      if (!modalRef.current) return;
      
      if (successMessage || errorMessage || infoMessage) {
        if (messageContainerRef.current) {
          const modalRect = modalRef.current.getBoundingClientRect();
          const messageRect = messageContainerRef.current.getBoundingClientRect();
          const scrollTop = modalRef.current.scrollTop + (messageRect.top - modalRect.top) - 20;
          
          modalRef.current.scrollTo({
            top: scrollTop,
            behavior: 'smooth'
          });
        } else {
          modalRef.current.scrollTo({
            top: 0,
            behavior: 'smooth'
          });
        }
      } else if (Object.keys(fieldErrors).length > 0) {
        // Scroll to first field error
        scrollToFirstError();
      }
    };

    if (successMessage || errorMessage || infoMessage || Object.keys(fieldErrors).length > 0) {
      const timer = setTimeout(() => {
        scrollToMessage();
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [successMessage, errorMessage, infoMessage, fieldErrors]);

  /* --------------------------------------------------------------------------
     Item strategy setter
     -------------------------------------------------------------------------- */
  const handleStrategyChange = (itemId, field, value) => {
    setItemStrategies((prev) => {
      const existing = prev[itemId] || {};
      if (existing[field] === value) return prev;
      return { ...prev, [itemId]: { ...existing, [field]: value } };
    });
    
    if (fieldErrors[`${itemId}-${field}`]) {
      setFieldErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[`${itemId}-${field}`];
        return newErrors;
      });
    }
  };

  /* --------------------------------------------------------------------------
     Pagination helpers
     -------------------------------------------------------------------------- */
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = (itemsToProcure || []).slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.max(1, Math.ceil((itemsToProcure || []).length / itemsPerPage));
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  /* --------------------------------------------------------------------------
     Validation: plan-level and item-level
     -------------------------------------------------------------------------- */
  const validateBeforeSubmit = () => {
    setFieldErrors({});
    
    if (!title?.trim() || !startDate || !endDate || !justification?.trim() || !procurementMethod) {
      setErrorMessage("Please fill in all required plan fields (title, start date, end date, justification, procurement method).");
      return false;
    }

    if (procurementMethod !== "Direct Purchase" && !planProcurementOfficer) {
      setErrorMessage("Please assign a procurement officer for the plan (required for this procurement method).");
      return false;
    }

    const todayISO = new Date().toISOString().split("T")[0];
    if (startDate < todayISO) {
      setErrorMessage("Start date cannot be in the past.");
      return false;
    }
    if (endDate < startDate) {
      setErrorMessage("End date cannot be before start date.");
      return false;
    }

    for (const item of itemsToProcure || []) {
      const strategy = itemStrategies[item._id] || {};

      if (!item.name || item.quantity === undefined || item.quantity === null) {
        setErrorMessage(`Item "${item.name || item._id}" is missing required data (name or quantity).`);
        return false;
      }

      if (!strategy.plannedOrderDate) {
        setFieldErrors(prev => ({
          ...prev,
          [`${item._id}-plannedOrderDate`]: `Planned order date is required for "${item.name}".`
        }));
        return false;
      }

      if (!strategy.recommendedSupplier) {
        setFieldErrors(prev => ({
          ...prev,
          [`${item._id}-recommendedSupplier`]: `Please select a supplier for "${item.name}".`
        }));
        return false;
      }

      const planned = strategy.plannedOrderDate;
      if (planned) {
        if (planned < startDate) {
          setFieldErrors(prev => ({
            ...prev,
            [`${item._id}-plannedOrderDate`]: `Planned order date for "${item.name}" cannot be before the plan start date (${formatDate(startDate)}).`
          }));
          return false;
        }
        if (planned > endDate) {
          setFieldErrors(prev => ({
            ...prev,
            [`${item._id}-plannedOrderDate`]: `Planned order date for "${item.name}" cannot be after the plan end date (${formatDate(endDate)}).`
          }));
          return false;
        }
        if (item.neededBy && new Date(planned) > new Date(item.neededBy)) {
          setFieldErrors(prev => ({
            ...prev,
            [`${item._id}-plannedOrderDate`]: `Planned order date for "${item.name}" must be on or before the item's needed-by date (${formatDate(item.neededBy)}).`
          }));
          return false;
        }
      }

      const allowedRisks = ["Low", "Medium", "High"];
      if (strategy.riskLevel && !allowedRisks.includes(strategy.riskLevel)) {
        setFieldErrors(prev => ({
          ...prev,
          [`${item._id}-riskLevel`]: `Invalid risk level for "${item.name}".`
        }));
        return false;
      }
    }

    setErrorMessage("");
    return true;
  };

  /* --------------------------------------------------------------------------
     Submit handler: builds payload and calls createPlan
     -------------------------------------------------------------------------- */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSuccessMessage("");
    setErrorMessage("");
    setInfoMessage("");
    setFieldErrors({});

    if (!validateBeforeSubmit()) {
      return;
    }

    const planData = {
      title,
      description,
      projectId: project?._id,
      startDate,
      endDate,
      procurementMethod,
      justification,
      procurementOfficer: planProcurementOfficer,
      requirements: (itemsToProcure || []).map((item) => {
        const strat = itemStrategies[item._id] || {};
        return {
          requirementId: item._id,
          recommendedSupplier: strat.recommendedSupplier || "",
          plannedOrderDate: strat.plannedOrderDate || "",
          riskLevel: strat.riskLevel || "Low",
          technicalApprover: strat.technicalApprover || "",
        };
      }),
      financials: {
        ...financials,
        subtotal,
        grandTotal,
      },
    };

    try {
      setSubmitting(true);
      const result = await createPlan(planData);
      setSuccessMessage(result.message || "Procurement plan created successfully!");
      setTimeout(() => onClose(true), 3000); // Updated to onClose(true)
    } catch (err) {
      const transformedError = transformApiError(err);
      
      if (typeof transformedError === 'object' && transformedError !== null && Object.keys(transformedError).length > 0) {
        setFieldErrors(transformedError);
        setErrorMessage("Please correct the errors below and try again.");
      } else {
        handleApiError(err, setErrorMessage);
      }
      
      console.error("Failed to create procurement plan:", err);
    } finally {
      setSubmitting(false);
    }
  };

  /* --------------------------------------------------------------------------
     If modal closed, render nothing
     -------------------------------------------------------------------------- */
  if (!isOpen) return null;

  /* --------------------------------------------------------------------------
     Render
     -------------------------------------------------------------------------- */
  return (
    <div className="fixed inset-0 bg-gray-900/50 z-50 flex items-start justify-center p-4 overflow-y-auto">
      <div ref={modalRef} className="bg-white rounded-lg w-full max-w-6xl my-8 shadow-lg max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center px-6 py-4 border-b">
          <div className="flex items-center gap-2">
            <FileText className="w-6 h-6 text-orange-600" />
            <h1 className="text-2xl font-medium text-orange-600">Create Procurement Plan</h1>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X />
          </button>
        </div>

        <form ref={formRef} onSubmit={handleSubmit} className="p-6 space-y-6">
          <div ref={messageContainerRef}>
            {successMessage && (
              <div className="bg-green-50 border-l-4 border-green-400 p-4 mb-2">
                <div className="flex">
                  <CheckCircle className="w-5 h-5 text-green-400" />
                  <div className="ml-3">
                    <p className="text-green-800">{successMessage}</p>
                  </div>
                </div>
              </div>
            )}
            {errorMessage && (
              <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-2">
                <div className="flex">
                  <Info className="w-5 h-5 text-red-400" />
                  <div className="ml-3">
                    <p className="text-red-800">{errorMessage}</p>
                    {fieldErrors && typeof fieldErrors === 'object' && Object.keys(fieldErrors).length > 0 && (
                      <ul className="mt-2 list-disc list-inside text-sm">
                        {Object.values(fieldErrors).map((error, index) => (
                          <li key={index} className="text-red-700">
                            {typeof error === 'string' ? error : JSON.stringify(error)}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              </div>
            )}
            {infoMessage && (
              <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-2">
                <div className="flex">
                  <Info className="w-5 h-5 text-blue-400" />
                  <div className="ml-3">
                    <p className="text-blue-800">{infoMessage}</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Project Information */}
          <div>
            <h2 className="text-lg font-medium text-gray-900 mb-4">Project Information</h2>
            <div className="bg-orange-50 p-4 rounded border-l-4 border-orange-400">
              <div className="mb-4">
                <h3 className="text-lg font-medium text-gray-900 mb-1">{project?.projectTitle}</h3>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">Location</span>
                  <p className="font-medium">{project?.location || "Not specified"}</p>
                </div>
                <div>
                  <span className="text-gray-500">Duration</span>
                  <p className="font-medium">{calculateDuration(project?.startDate, project?.endDate)}</p>
                </div>
                <div>
                  <span className="text-gray-500">Status</span>
                  <p className="font-medium capitalize">{project?.status || "Not specified"}</p>
                </div>
                <div>
                  <span className="text-gray-500">Progress</span>
                  <p className="font-medium">
                    {project?.overallProgress !== undefined ? `${project.overallProgress}%` : "N/A"}
                  </p>
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-orange-200">
                <span className="text-gray-500 text-sm">Project Timeline</span>
                <p className="font-medium">
                  {formatDate(project?.startDate)} → {formatDate(project?.endDate)}
                </p>
              </div>
              {project?.donor && project.donor.length > 0 && (
                <div className="mt-4 pt-4 border-t border-orange-200">
                  <span className="text-gray-500 text-sm">Donors</span>
                  <p className="font-medium">{project.donor.map((d) => d.name || d).join(", ")}</p>
                </div>
              )}
              {dateConstraints.earliestNeededBy && (
                <div className="mt-4 pt-4 border-t border-orange-200">
                  <p className="text-sm text-orange-600 mt-2">
                    ⏰ Earliest item needed by: {dateConstraints.earliestNeededBy.toLocaleDateString()}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Plan Details */}
          <div>
            <h2 className="text-lg font-medium text-gray-900 mb-4">Plan Details</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Plan Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  required
                  disabled={!!successMessage}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Plan Description</label>
                <input
                  type="text"
                  className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  disabled={!!successMessage}
                />
              </div>
            </div>
          </div>

          {/* Timeline */}
          <div>
            <h2 className="text-lg font-medium text-gray-900 mb-4">Timeline</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Plan Start Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  required
                  min={new Date().toISOString().split("T")[0]}
                  disabled={!!successMessage}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Plan End Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  required
                  min={startDate || new Date().toISOString().split("T")[0]}
                  disabled={!!successMessage}
                />
              </div>
            </div>
            {dateHints.length > 0 && (
              <div className="bg-blue-50 border-l-4 border-blue-400 p-3">
                <div className="text-sm">
                  {dateHints.map((hint, index) => (
                    <p key={index} className="text-blue-700">
                      {hint}
                    </p>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Strategy */}
          <div>
            <h2 className="text-lg font-medium text-gray-900 mb-4">Strategy</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Procurement Method <span className="text-red-500">*</span>
                </label>
                <select
                  className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  value={procurementMethod}
                  onChange={(e) => setProcurementMethod(e.target.value)}
                  required
                  disabled={!!successMessage}
                >
                  <option value="Request for Quotation (RFQ)">Request for Quotation (RFQ)</option>
                  <option value="Open Tender">Open Tender</option>
                  <option value="Restricted Tender">Restricted Tender</option>
                  <option value="Direct Purchase">Direct Purchase</option>
                  <option value="Framework Agreement">Framework Agreement</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Procurement Officer <span className="text-red-500">*</span>
                </label>
                <select
                  className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  value={planProcurementOfficer}
                  onChange={(e) => setPlanProcurementOfficer(e.target.value)}
                  required={procurementMethod !== "Direct Purchase"}
                  disabled={!!successMessage}
                >
                  <option value="">Select Officer</option>
                  {procurementOfficers.length > 0 ? (
                    procurementOfficers.map((officer) => (
                      <option key={officer._id} value={officer._id}>
                        {officer.firstName} {officer.lastName}
                      </option>
                    ))
                  ) : (
                    <option value="" disabled>
                      No officers found
                    </option>
                  )}
                </select>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Procurement Method Justification <span className="text-red-500">*</span>
                </label>
                <textarea
                  className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 resize-none"
                  rows={3}
                  value={justification}
                  onChange={(e) => setJustification(e.target.value)}
                  required
                  disabled={!!successMessage}
                />
              </div>
            </div>
          </div>

          {/* Items & Sourcing */}
          <div>
            <h2 className="text-lg font-medium text-gray-900 mb-4">Items & Sourcing</h2>
            <div className="mb-4 p-3 bg-yellow-50 border-l-4 border-yellow-400">
              <p className="text-sm text-yellow-800">
                <strong>Note:</strong> All fields in the items table are required. Please ensure you select a supplier and enter a planned order date for each item.
              </p>
            </div>

            {/* Vendor filter */}
            <div className="flex items-center gap-4 mb-4 p-3 bg-orange-50 rounded">
              <label className="text-sm font-medium text-gray-700">Filter by Category:</label>
              <select
                className="border border-gray-300 rounded px-3 py-1 text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                disabled={!!successMessage}
              >
                <option value="">All Categories</option>
                <option value="service_provider">Service Provider</option>
                <option value="goods_supplier">Goods Supplier</option>
                <option value="consultant">Consultant</option>
              </select>
              <span className="text-sm text-gray-600">{filteredVendors.length} vendors available</span>
            </div>

            {/* Items table */}
            <div className="border border-gray-300 rounded overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-orange-50">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium text-gray-700 border-b">Item</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-700 border-b">Qty/Cost</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-700 border-b">
                      Supplier<span className="text-red-500">*</span>
                    </th>
                    <th className="px-3 py-2 text-left font-medium text-gray-700 border-b">
                      Order Date<span className="text-red-500">*</span>
                    </th>
                    <th className="px-3 py-2 text-left font-medium text-gray-700 border-b">Risk</th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-200">
                  {currentItems.map((item) => {
                    const strategy = itemStrategies[item._id] || {};
                    const recommendedId = strategy.recommendedSupplier;
                    const selectedVendor = recommendedId ? vendors.find((v) => v._id === recommendedId) : null;
                    
                    const orderDateError = fieldErrors[`${item._id}-plannedOrderDate`];
                    const supplierError = fieldErrors[`${item._id}-recommendedSupplier`];
                    const riskError = fieldErrors[`${item._id}-riskLevel`];

                    return (
                      <tr key={item._id} className="hover:bg-gray-50">
                        <td className="px-3 py-3">
                          <div className="font-medium text-gray-900">{item.name}</div>
                          {item.specification && <div className="text-xs text-gray-500 mt-1">{item.specification}</div>}
                        </td>
                        <td className="px-3 py-3">
                          <div>
                            {item.quantity} {item.unit}
                          </div>
                          <div className="font-medium">${(item.totalCost || 0).toLocaleString()}</div>
                        </td>
                        <td className="px-3 py-3 relative">
                          <div
                            onMouseEnter={(e) => handleVendorHoverEnter(e, item._id)}
                            onMouseLeave={handleVendorHoverLeave}
                            className="relative"
                          >
                            <select
                              className={`w-full border rounded px-2 py-1 text-sm focus:ring-1 focus:ring-orange-500 ${
                                supplierError ? 'border-red-500 bg-red-50' : 'border-gray-300'
                              }`}
                              value={strategy.recommendedSupplier || ""}
                              onChange={(e) => handleStrategyChange(item._id, "recommendedSupplier", e.target.value)}
                              required
                              disabled={!!successMessage}
                            >
                              <option value="">Select Supplier</option>
                              {filteredVendors.map((vendor) => (
                                <option key={vendor._id} value={vendor._id}>
                                  {vendor.name}
                                </option>
                              ))}
                            </select>

                            {supplierError && (
                              <p 
                                className="mt-1 text-xs text-red-600 error-message"
                                data-error-for={`${item._id}-recommendedSupplier`}
                              >
                                {supplierError}
                              </p>
                            )}

                            {hoveredVendorId === item._id && selectedVendor && (
                              <HoverCardPortal>
                                <div
                                  className="w-72 p-4 bg-white border border-gray-200 rounded-lg shadow-xl z-50 fixed transition-all duration-100 ease-in-out"
                                  style={{
                                    top:
                                      hoverCardPosition.position === "bottom"
                                        ? `${hoverCardPosition.y + 10}px`
                                        : "auto",
                                    bottom:
                                      hoverCardPosition.position === "top"
                                        ? `${window.innerHeight - hoverCardPosition.y + 10}px`
                                        : "auto",
                                    left: `${hoverCardPosition.x}px`,
                                    pointerEvents: "none",
                                  }}
                                >
                                  <div className="flex items-center mb-2">
                                    <ClipboardList className="w-5 h-5 text-orange-500 mr-2" />
                                    <h3 className="font-bold text-lg text-gray-800 leading-tight">{selectedVendor.name}</h3>
                                  </div>
                                  <p className="text-xs text-gray-500 mb-3 ml-7 -mt-1">Vendor Code: {selectedVendor.vendorCode}</p>
                                  <div className="space-y-2 text-sm text-gray-700">
                                    <div className="flex items-center">
                                      <MapPin className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0" />
                                      <span>
                                        <span className="font-medium text-gray-800">Category: </span>
                                        <span className="capitalize">{selectedVendor.category?.replace("_", " ") || "N/A"}</span>
                                      </span>
                                    </div>
                                    {selectedVendor.contactPerson && (
                                      <div className="flex items-center">
                                        <Users className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0" />
                                        <span>
                                          <span className="font-medium text-gray-800">Contact: </span>
                                          {selectedVendor.contactPerson}
                                        </span>
                                      </div>
                                    )}
                                    {selectedVendor.phone && (
                                      <div className="flex items-center">
                                        <Phone className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0" />
                                        <span>
                                          <span className="font-medium text-gray-800">Phone: </span>
                                          {selectedVendor.phone}
                                        </span>
                                      </div>
                                    )}
                                    {selectedVendor.email && (
                                      <div className="flex items-center">
                                        <Mail className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0" />
                                        <span>
                                          <span className="font-medium text-gray-800">Email: </span>
                                          {selectedVendor.email}
                                        </span>
                                      </div>
                                    )}
                                    <div className="flex items-center">
                                      <Info className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0" />
                                      <span>
                                        <span className="font-medium text-gray-800">Status: </span>
                                        <span
                                          className={`font-medium capitalize ${
                                            selectedVendor.status === "approved" ? "text-green-600" : selectedVendor.status === "pending" ? "text-yellow-600" : "text-red-600"
                                          }`}
                                        >
                                          {selectedVendor.status}
                                        </span>
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              </HoverCardPortal>
                            )}
                          </div>
                        </td>
                        <td className="px-3 py-3">
                          <div className="relative">
                            <input
                              type="date"
                              className={`w-full border rounded px-2 py-1 text-sm focus:ring-1 focus:ring-orange-500 ${
                                orderDateError ? 'border-red-500 bg-red-50' : 'border-gray-300'
                              }`}
                              value={strategy.plannedOrderDate || ""}
                              onChange={(e) => handleStrategyChange(item._id, "plannedOrderDate", e.target.value)}
                              required
                              min={startDate || new Date().toISOString().split("T")[0]}
                              max={endDate || ""}
                              disabled={!!successMessage}
                            />
                            {orderDateError && (
                              <p 
                                className="mt-1 text-xs text-red-600 error-message"
                                data-error-for={`${item._id}-plannedOrderDate`}
                              >
                                {orderDateError}
                              </p>
                            )}
                          </div>
                        </td>
                        <td className="px-3 py-3">
                          <select
                            className={`w-full border rounded px-2 py-1 text-sm focus:ring-1 focus:ring-orange-500 ${
                              riskError ? 'border-red-500 bg-red-50' : 'border-gray-300'
                            }`}
                            value={strategy.riskLevel || "Low"}
                            onChange={(e) => handleStrategyChange(item._id, "riskLevel", e.target.value)}
                            disabled={!!successMessage}
                          >
                            <option value="Low">Low</option>
                            <option value="Medium">Medium</option>
                            <option value="High">High</option>
                          </select>
                          {riskError && (
                            <p 
                              className="mt-1 text-xs text-red-600 error-message"
                              data-error-for={`${item._id}-riskLevel`}
                            >
                              {riskError}
                            </p>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {(itemsToProcure || []).length > itemsPerPage && (
              <div className="flex justify-between items-center mt-4">
                <button
                  onClick={() => paginate(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="px-4 py-2 border rounded text-gray-700 disabled:opacity-50"
                >
                  Previous
                </button>
                <span className="text-sm text-gray-600">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() => paginate(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="px-4 py-2 border rounded text-orange-600 disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            )}
          </div>

          {/* Financial Summary */}
          <div>
            <h2 className="text-lg font-medium text-gray-900 mb-4">Financial Summary</h2>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Subtotal</label>
                <div className="w-full border border-gray-300 rounded px-3 py-2 bg-orange-50 font-medium">${subtotal.toLocaleString()}</div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Taxes</label>
                <input
                  type="number"
                  min={0}
                  step="0.01"
                  className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  value={financials.taxes}
                  onChange={(e) => setFinancials((prev) => ({ ...prev, taxes: Number(e.target.value) }))}
                  disabled={!!successMessage}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Contingency</label>
                <input
                  type="number"
                  min={0}
                  step="0.01"
                  className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  value={financials.contingency}
                  onChange={(e) => setFinancials((prev) => ({ ...prev, contingency: Number(e.target.value) }))}
                  disabled={!!successMessage}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Grand Total</label>
                <div className="w-full border-2 border-orange-500 rounded px-3 py-2 bg-orange-50 font-bold text-orange-800">
                  ${grandTotal.toLocaleString()}
                </div>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
            >
              {successMessage ? "Close" : "Cancel"}
            </button>
            {!successMessage && (
              <button
                type="submit"
                disabled={submitting}
                className="px-6 py-2 bg-orange-600 text-white rounded hover:bg-orange-700 disabled:opacity-50"
              >
                {submitting ? "Creating..." : "Create Plan"}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}